import React, { useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../../config/constants';
import { generateToken, TOKEN_HEADER } from '../../utils/tokenUtils';
import '../../styles/event-feedback.css';

const STAR_VALUES = [1, 2, 3, 4, 5];

function StarRating({ label, value, onChange, fieldName }) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="event-feedback-v2-field">
      <label className="event-feedback-v2-label">{label}</label>
      <div className="event-feedback-v2-stars" role="group" aria-label={label}>
        {STAR_VALUES.map((star) => (
          <button
            key={`${fieldName}-${star}`}
            type="button"
            className={`event-feedback-v2-star ${(hovered || value) >= star ? 'active' : ''}`}
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            aria-label={`${label}: ${star} star${star > 1 ? 's' : ''}`}
          >
            ★
          </button>
        ))}
      </div>
    </div>
  );
}

export default function EventFeedbackForm({ initialEventName = '' }) {
  const [formData, setFormData] = useState({
    eventName: initialEventName,
    name: '',
    email: '',
    mobile: '',
    overallRating: 0,
    comments: '',
    sessionSpeedRating: 0,
    contentQualityRating: 0,
    trainerRating: 0,
    willingToRefer: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const isFormValid = useMemo(() => {
    return (
      formData.eventName.trim() &&
      formData.name.trim() &&
      formData.email.trim() &&
      formData.mobile.trim() &&
      formData.overallRating > 0 &&
      formData.sessionSpeedRating > 0 &&
      formData.contentQualityRating > 0 &&
      formData.trainerRating > 0 &&
      (formData.willingToRefer === 'yes' || formData.willingToRefer === 'no')
    );
  }, [formData]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isFormValid) {
      setMessageType('error');
      setMessage('Please complete all required fields and ratings.');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      const response = await fetch(API_ENDPOINTS.EVENT_FEEDBACK_SUBMIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken()
        },
        body: JSON.stringify({
          eventName: formData.eventName.trim(),
          name: formData.name.trim(),
          email: formData.email.trim(),
          mobile: formData.mobile.trim(),
          overallRating: formData.overallRating,
          comments: formData.comments.trim(),
          sessionSpeedRating: formData.sessionSpeedRating,
          contentQualityRating: formData.contentQualityRating,
          trainerRating: formData.trainerRating,
          willingToRefer: formData.willingToRefer === 'yes'
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to submit event feedback.');
      }

      setMessageType('success');
      setMessage('Thanks for sharing your feedback. We appreciate your response.');
      setFormData((prev) => ({
        eventName: prev.eventName,
        name: '',
        email: '',
        mobile: '',
        overallRating: 0,
        comments: '',
        sessionSpeedRating: 0,
        contentQualityRating: 0,
        trainerRating: 0,
        willingToRefer: ''
      }));
    } catch (error) {
      setMessageType('error');
      setMessage(error.message || 'Unable to submit feedback right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="event-feedback-v2-form" onSubmit={handleSubmit}>
      <div className="event-feedback-v2-grid">
        <div className="event-feedback-v2-field">
          <label htmlFor="eventName" className="event-feedback-v2-label">Event name</label>
          <input
            id="eventName"
            type="text"
            value={formData.eventName}
            onChange={(e) => updateField('eventName', e.target.value)}
            className="event-feedback-v2-input"
            maxLength={255}
            required
          />
        </div>

        <div className="event-feedback-v2-field">
          <label htmlFor="name" className="event-feedback-v2-label">Name</label>
          <input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            className="event-feedback-v2-input"
            maxLength={255}
            required
          />
        </div>

        <div className="event-feedback-v2-field">
          <label htmlFor="email" className="event-feedback-v2-label">Email</label>
          <input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => updateField('email', e.target.value)}
            className="event-feedback-v2-input"
            maxLength={255}
            required
          />
        </div>

        <div className="event-feedback-v2-field">
          <label htmlFor="mobile" className="event-feedback-v2-label">Mobile</label>
          <input
            id="mobile"
            type="tel"
            value={formData.mobile}
            onChange={(e) => updateField('mobile', e.target.value)}
            className="event-feedback-v2-input"
            placeholder="+919999999999"
            required
          />
        </div>
      </div>

      <StarRating
        label="Overall rating (1-5 stars)"
        value={formData.overallRating}
        onChange={(value) => updateField('overallRating', value)}
        fieldName="overallRating"
      />

      <div className="event-feedback-v2-field">
        <label htmlFor="comments" className="event-feedback-v2-label">Comments</label>
        <textarea
          id="comments"
          value={formData.comments}
          onChange={(e) => updateField('comments', e.target.value)}
          className="event-feedback-v2-textarea"
          rows={4}
          placeholder="Share what worked well and what we can improve."
        />
      </div>

      <StarRating
        label="Speed of session"
        value={formData.sessionSpeedRating}
        onChange={(value) => updateField('sessionSpeedRating', value)}
        fieldName="sessionSpeedRating"
      />

      <StarRating
        label="Content quality (1-5 stars)"
        value={formData.contentQualityRating}
        onChange={(value) => updateField('contentQualityRating', value)}
        fieldName="contentQualityRating"
      />

      <StarRating
        label="Trainer rating (1-5 stars)"
        value={formData.trainerRating}
        onChange={(value) => updateField('trainerRating', value)}
        fieldName="trainerRating"
      />

      <div className="event-feedback-v2-field">
        <span className="event-feedback-v2-label">Are you willing to refer us?</span>
        <div className="event-feedback-v2-radio-row">
          <label className="event-feedback-v2-radio">
            <input
              type="radio"
              name="willingToRefer"
              checked={formData.willingToRefer === 'yes'}
              onChange={() => updateField('willingToRefer', 'yes')}
            />
            Yes
          </label>
          <label className="event-feedback-v2-radio">
            <input
              type="radio"
              name="willingToRefer"
              checked={formData.willingToRefer === 'no'}
              onChange={() => updateField('willingToRefer', 'no')}
            />
            No
          </label>
        </div>
      </div>

      <button className="event-feedback-v2-submit" type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Submitting...' : 'Submit event feedback'}
      </button>

      {message && (
        <p className={`event-feedback-v2-message ${messageType === 'error' ? 'error' : 'success'}`}>
          {message}
        </p>
      )}
    </form>
  );
}
